import React, { useEffect, useState } from 'react';

interface VersionInfo {
  sha: string;
  branch: string;
  builtAt: string;
}

/**
 * Small, unobtrusive build-version indicator. Fetches /version.json (written
 * by scripts/writeVersion.js at start/build time, never bundled into the JS
 * so a stale cached bundle can't show a stale version) and renders the short
 * commit SHA + build date — lets whoever's looking at a deployed environment
 * confirm which commit is actually live, without checking server logs.
 *
 * Renders nothing if the fetch fails (e.g. an older cached build without
 * version.json) rather than showing broken/placeholder text.
 */
export const VersionBadge: React.FC<{ className?: string }> = ({ className }) => {
  const [info, setInfo] = useState<VersionInfo | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetch('/version.json', { cache: 'no-store' })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => { if (!cancelled && data) setInfo(data); })
      .catch(() => { /* no version.json available — render nothing */ });
    return () => { cancelled = true; };
  }, []);

  if (!info) return null;

  const builtDate = new Date(info.builtAt);
  const dateLabel = Number.isNaN(builtDate.getTime())
    ? info.builtAt
    : builtDate.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });

  return (
    <div
      className={className}
      title={`Branch ${info.branch} · built ${info.builtAt}`}
      style={{ fontSize: '10px', color: 'var(--color-text-tertiary, #9aa3af)', marginTop: '4px', textAlign: 'center' }}
    >
      v{info.sha} · {dateLabel}
    </div>
  );
};

export default VersionBadge;
